import { MetricExploreChart } from '@/components/charts/MetricExploreChart';
import { SeriesRail } from '@/components/metrics/SeriesRail';
import { Island } from '@/components/shell/Island';
import { Skeleton } from '@/components/ui/skeleton';
import {
  decodeSeries,
  defaultStateFor,
  encodeSeries,
  isExploreMetricKey,
  newSeriesId,
  parseBucket,
  parseRange,
} from '@/lib/explore-url';
import { useMetricExplore } from '@/lib/queries/explore';
import { useSites } from '@/lib/queries/sites';
/**
 * /metrics/explore — multi-series metric explorer.
 *
 * The page state lives entirely in the URL search params (see
 * `lib/explore-url.ts`). React state mirrors the URL — every edit
 * funnels through `commit()` which pushes a new URL and lets the
 * route component re-derive from `Route.useSearch()`. This gives
 * free back-button support + a single source of truth for the
 * eventual `metric_views.config` jsonb (TODO note below).
 *
 * Hover entry-points from MetricTile land here pre-filled with one
 * series — when no `series=` is present, the route reads other
 * search params (metric, site_uuid, page_uuid, device, region) and
 * synthesises a default series via `defaultStateFor()`.
 *
 * **Direct-nav (no entry-context):** when the URL carries no
 * `site=` param, the default series starts with an empty
 * `site_uuid`. A one-shot effect fills it with the user's first
 * accessible site once `useSites()` resolves, so the page never
 * sits in an indefinite "needs configuration" state.
 *
 * Future trajectory — saved views:
 *   When `metric_views` lands, the [Save view…] button serialises
 *   exactly the URL-state shape into `config jsonb`. Loading is the
 *   inverse. DO NOT diverge this codec from the eventual save shape
 *   or the migration will need a transform layer nobody wants to
 *   write.
 */
import {
  ALLOWED_BUCKETS_BY_RANGE,
  BUCKETS,
  type Bucket,
  EXPLORE_METRIC_KEYS,
  type ExploreMetricKey,
  type ExploreRequest,
  MAX_SERIES_UI_RECOMMENDED,
  RANGES,
  type Range,
  type Series,
  VISUALISATIONS,
  type Visualisation,
  approximatePointsPerSeries,
  defaultBucketFor,
  defaultVizFor,
  isBucketAllowedFor,
  validateExploreShape,
} from '@clickweave/shared';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

// ─── Search-param schema ───────────────────────────────────────────────────

interface ExploreSearch {
  series?: string; // base64-encoded JSON array of Series
  range?: Range;
  bucket?: Bucket;
  /**
   * Chart shape. Absent → derive from series via `defaultVizFor()`
   * (scatter when any series is raw, line otherwise). Present →
   * user's explicit pick wins forever.
   */
  viz?: Visualisation;
  // Initial seed when `series` is absent (from MetricTile hover-entry).
  metric?: ExploreMetricKey;
  site?: string;
  page?: string;
  device?: 'mobile' | 'desktop';
  region?: string;
}

function validateSearch(raw: Record<string, unknown>): ExploreSearch {
  const out: ExploreSearch = {};
  if (typeof raw.series === 'string') out.series = raw.series;
  if (typeof raw.range === 'string' && (RANGES as readonly string[]).includes(raw.range)) {
    out.range = raw.range as Range;
  }
  if (typeof raw.bucket === 'string' && (BUCKETS as readonly string[]).includes(raw.bucket)) {
    out.bucket = raw.bucket as Bucket;
  }
  if (typeof raw.viz === 'string' && (VISUALISATIONS as readonly string[]).includes(raw.viz)) {
    out.viz = raw.viz as Visualisation;
  }
  if (typeof raw.metric === 'string' && isExploreMetricKey(raw.metric)) {
    out.metric = raw.metric;
  }
  if (typeof raw.site === 'string' && raw.site.startsWith('site-')) out.site = raw.site;
  if (typeof raw.page === 'string' && raw.page.startsWith('pg-')) out.page = raw.page;
  if (raw.device === 'mobile' || raw.device === 'desktop') out.device = raw.device;
  if (typeof raw.region === 'string') out.region = raw.region;
  return out;
}

export const Route = createFileRoute('/_app/metrics/explore')({
  validateSearch,
  component: MetricsExplore,
});

// ─── Component ─────────────────────────────────────────────────────────────

function MetricsExplore() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const sites = useSites();

  // Derive the initial state from URL params. Memoise so we don't recompute
  // on every render — the state itself lives in useState below.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only by design (see note below the return)
  const initial = useMemo(() => {
    const decoded = search.series ? decodeSeries(search.series) : null;
    if (decoded && decoded.length > 0) {
      return {
        series: decoded,
        range: parseRange(search.range),
        bucket: parseBucket(search.bucket),
      };
    }
    // No (or invalid) series= — seed from the navigation context.
    return defaultStateFor({
      metric: search.metric ?? 'lcp',
      site_uuid: search.site ?? '',
      ...(search.page ? { page_uuid: search.page } : {}),
      ...(search.device ? { device: search.device } : {}),
      ...(search.region ? { region: search.region } : {}),
    });
    // We *want* `initial` to be tied to the URL params on mount only —
    // subsequent edits flow through `commit()` below, not through this
    // memo. Re-deriving here on URL change would clobber in-progress
    // edits the user hasn't yet committed.
  }, []);

  const [seriesList, setSeriesList] = useState<Series[]>(initial.series);
  const [range, setRange] = useState<Range>(initial.range);
  const [bucket, setBucket] = useState<Bucket>(initial.bucket);
  const [mutedIds, setMutedIds] = useState<Set<string>>(() => new Set());
  const [axisOverrides, setAxisOverrides] = useState<Record<string, 'primary' | 'secondary'>>({});
  /**
   * `vizExplicit` holds the user's last picked visualisation. `null`
   * means "no explicit pick" — the effective viz then falls back to
   * `defaultVizFor(seriesList)` (scatter when any series is raw, line
   * otherwise). Splitting the storage like this lets a user who hasn't
   * touched the pill ride the auto-default as they toggle raw on and
   * off, but once they pick a shape it sticks.
   */
  const [vizExplicit, setVizExplicit] = useState<Visualisation | null>(search.viz ?? null);

  // Stable color index per series id. Insertion order = palette order.
  const colorIndexById = useMemo(() => {
    const out: Record<string, number> = {};
    seriesList.forEach((s, i) => {
      out[s.id] = i;
    });
    return out;
  }, [seriesList]);

  // ── State mutators that also sync the URL ────────────────────────────
  const commit = (
    nextSeries: Series[],
    nextRange?: Range,
    nextBucket?: Bucket,
    nextVizExplicit?: Visualisation | null,
  ) => {
    const r = nextRange ?? range;
    const b = nextBucket ?? bucket;
    const v = nextVizExplicit === undefined ? vizExplicit : nextVizExplicit;
    setSeriesList(nextSeries);
    setRange(r);
    setBucket(b);
    setVizExplicit(v);
    navigate({
      search: () => ({
        series: encodeSeries(nextSeries),
        range: r,
        bucket: b,
        ...(v ? { viz: v } : {}),
      }),
      replace: true,
    });
  };

  // ── Auto-fill default site once the sites list resolves ──────────────
  //
  // Direct-nav (no `?site=` in URL) leaves the default series with an
  // empty site_uuid, which gates the query off and used to render an
  // indefinite skeleton. Pre-fill the first accessible site as soon as
  // useSites() returns so the chart appears immediately. Only fires
  // once — subsequent edits are user-owned and don't re-trigger this.
  const firstSiteUuid = sites.data?.sites?.[0]?.uuid;
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate one-shot
  useEffect(() => {
    if (!firstSiteUuid) return;
    const needsFill = seriesList.some((s) => !s.site_uuid);
    if (!needsFill) return;
    commit(seriesList.map((s) => (s.site_uuid ? s : { ...s, site_uuid: firstSiteUuid })));
  }, [firstSiteUuid]);

  const onAddSeries = () => {
    // UI cap is intentionally lower than the server cap — see
    // MAX_SERIES_UI_RECOMMENDED. Power users can still hit the server
    // cap by pasting a URL with more series in it; this just removes
    // the easy path to accidentally building an N-series wall of
    // colours nobody can read.
    if (seriesList.length >= MAX_SERIES_UI_RECOMMENDED) return;
    // New series copies the last one's facets — much faster than
    // re-picking metric + site every time.
    const last = seriesList[seriesList.length - 1];
    const next: Series = last
      ? { ...last, id: newSeriesId() }
      : {
          id: newSeriesId(),
          metric: 'lcp',
          site_uuid: firstSiteUuid ?? '',
          percentile: 'p75',
        };
    commit([...seriesList, next]);
  };

  const onChangeSeries = (id: string, next: Series) => {
    commit(seriesList.map((s) => (s.id === id ? next : s)));
  };

  const onRemoveSeries = (id: string) => {
    if (seriesList.length === 1) return; // never let it hit 0 — keeps the chart non-empty
    commit(seriesList.filter((s) => s.id !== id));
  };

  const onDuplicateSeries = (id: string) => {
    if (seriesList.length >= MAX_SERIES_UI_RECOMMENDED) return;
    const source = seriesList.find((s) => s.id === id);
    if (!source) return;
    const index = seriesList.findIndex((s) => s.id === id);
    const dup: Series = { ...source, id: newSeriesId() };
    const next = [...seriesList];
    next.splice(index + 1, 0, dup);
    commit(next);
  };

  const onToggleMute = (id: string) => {
    setMutedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onToggleAxis = (id: string) => {
    setAxisOverrides((prev) => {
      const next = { ...prev };
      if (next[id] === 'secondary') delete next[id];
      else next[id] = 'secondary';
      return next;
    });
  };

  // Per-series site picking lives in SeriesEditor; the route doesn't
  // own a global "Site" scope anymore. A previous version of the header
  // strip exposed a single Site pill that would collapse every series
  // to one site, which broke cross-site comparison the moment a user
  // touched it — pointing at "Site A" forced the side-by-side comparison
  // they were building back to a single site. Removed 2026-05-28; the
  // per-series picker is the only correct affordance because each
  // series carries its own site.

  // ── Build the API request + shape-validate before firing ─────────────
  const request: ExploreRequest = { series: seriesList, range, bucket };
  const shapeError = validateExploreShape(request);
  // Series with an empty `site_uuid` (just-created via Add, or the
  // brief moment before the auto-fill effect runs) won't validate
  // server-side — gate the query until everything has a site.
  const allSeriesHaveSites = seriesList.every((s) => !!s.site_uuid);
  const queryEnabled = !shapeError && allSeriesHaveSites;
  const explore = useMetricExplore(request, { enabled: queryEnabled });

  const resolved = useMemo(() => {
    const out: Record<string, NonNullable<typeof explore.data>['series'][number]> = {};
    for (const r of explore.data?.series ?? []) out[r.id] = r;
    return out;
  }, [explore.data]);

  // Effective viz — user's explicit pick if any, else derive from
  // series. Recomputing on every render is fine; the inputs are small.
  const effectiveViz: Visualisation = vizExplicit ?? defaultVizFor(seriesList);

  // ── Header controls ──────────────────────────────────────────────────
  //
  // The bucket dropdown is driven off the current range via
  // `ALLOWED_BUCKETS_BY_RANGE` — picking 30d removes 5m/15m/1h from
  // the bucket list, not because they're invalid bucket values but
  // because the (30d, <1d) combinations scan too much data. Changing
  // range auto-snaps bucket to a sane default for the new range, so
  // users never strand themselves on an orphan combination.
  const allowedBuckets = ALLOWED_BUCKETS_BY_RANGE[range];
  const onRangeChange = (next: Range) => {
    // If the current bucket isn't allowed for the new range, snap to
    // the default (most-granular safe) bucket for that range.
    const nextBucket = isBucketAllowedFor(next, bucket) ? bucket : defaultBucketFor(next);
    commit(seriesList, next, nextBucket);
  };
  const siteOptions = sites.data?.sites ?? [];
  const headerControls = (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Pill label="Range">
        <select
          value={range}
          onChange={(e) => onRangeChange(e.target.value as Range)}
          className={pillSelectClass}
        >
          {RANGES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Pill>
      <Pill label="Bucket">
        <select
          value={bucket}
          onChange={(e) => commit(seriesList, range, e.target.value as Bucket)}
          className={pillSelectClass}
        >
          {allowedBuckets.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </Pill>
      <Pill label="Viz">
        <select
          value={effectiveViz}
          onChange={(e) => commit(seriesList, range, bucket, e.target.value as Visualisation)}
          className={pillSelectClass}
        >
          {VISUALISATIONS.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Pill>
      <span className="text-[var(--color-mute)]">
        ~{approximatePointsPerSeries(range, bucket)} pts
      </span>
    </div>
  );

  // ── Body slot logic ──────────────────────────────────────────────────
  //
  // Four mutually exclusive states; previously they were rendered in
  // parallel which left the skeleton visible even when the query was
  // gated off (the "indefinite shimmer" bug).
  let body: React.ReactElement;
  if (explore.data) {
    body = (
      <MetricExploreChart
        series={explore.data.series}
        mutedIds={mutedIds}
        colorIndexById={colorIndexById}
        axisOverrides={axisOverrides}
        viz={effectiveViz}
        onLegendClick={onToggleMute}
        onPointClick={(_id, point) => {
          if (point.audit_uuid) {
            // Click-through to audit detail — only meaningful for raw series.
            window.location.assign(`/settings/logs?audit=${encodeURIComponent(point.audit_uuid)}`);
          }
        }}
      />
    );
  } else if (explore.isError) {
    body = (
      <EmptyChartFrame tone="danger">
        Couldn't load explore data: {String((explore.error as Error)?.message ?? explore.error)}
      </EmptyChartFrame>
    );
  } else if (queryEnabled && explore.isLoading) {
    body = <Skeleton className="h-[400px] w-full rounded-md" />;
  } else if (!allSeriesHaveSites && sites.isLoading) {
    // Waiting for the auto-fill effect — sites list hasn't returned yet.
    body = <Skeleton className="h-[400px] w-full rounded-md" />;
  } else if (!allSeriesHaveSites) {
    body = (
      <EmptyChartFrame>
        {siteOptions.length === 0
          ? 'No sites available. Create one to start exploring metrics.'
          : 'Pick a site in the series editor on the right to start drawing the chart.'}
      </EmptyChartFrame>
    );
  } else if (shapeError) {
    body = <EmptyChartFrame tone="warning">{shapeError}</EmptyChartFrame>;
  } else {
    body = <EmptyChartFrame>No data for the current selection.</EmptyChartFrame>;
  }

  return (
    <Island breadcrumb={[{ label: 'Metrics' }, { label: 'Explore' }]} actions={headerControls}>
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {body}
          {(explore.data?.warnings ?? []).length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-[var(--color-mute)]">
              {explore.data?.warnings.map((w, i) => (
                <li key={`${w.code}-${i}`}>• {w.message}</li>
              ))}
            </ul>
          )}
        </div>
        <aside className="w-full lg:w-72 lg:shrink-0">
          <SeriesRail
            series={seriesList}
            resolved={resolved}
            mutedIds={mutedIds}
            colorIndexById={colorIndexById}
            axisOverrides={axisOverrides}
            onChangeSeries={onChangeSeries}
            onRemoveSeries={onRemoveSeries}
            onDuplicateSeries={onDuplicateSeries}
            onToggleMute={onToggleMute}
            onToggleAxis={onToggleAxis}
            onAddSeries={onAddSeries}
          />
          <p className="mt-3 text-[10px] text-[var(--color-mute)]">
            v1 supports {EXPLORE_METRIC_KEYS.length} metrics (Web Vitals + Lighthouse scores). Page
            weight + execution metrics need a Tinybird datasource extension — tracked separately.{' '}
            <Link to="/docs" className="underline">
              Docs
            </Link>
          </p>
        </aside>
      </div>
    </Island>
  );
}

// ─── Small styled atoms ────────────────────────────────────────────────────

function Pill({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2 py-1">
      <span className="text-[var(--color-mute)]">{label}</span>
      {children}
    </span>
  );
}

const pillSelectClass = 'bg-transparent text-[var(--color-ink)] tabular-nums focus:outline-none';

/**
 * Chart-shaped empty/error frame. Sits in the same vertical slot as the
 * chart so layout doesn't jump between states.
 */
function EmptyChartFrame({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'warning' | 'danger';
  children: React.ReactNode;
}): React.ReactElement {
  const ring =
    tone === 'danger'
      ? 'border-[var(--color-danger-soft)] bg-[var(--color-danger-soft)]/30 text-[var(--color-ink-soft)]'
      : tone === 'warning'
        ? 'border-[var(--color-warning-soft)] bg-[var(--color-warning-soft)]/30 text-[var(--color-ink-soft)]'
        : 'border-[var(--color-line)] bg-[var(--color-paper-alt)] text-[var(--color-mute)]';
  return (
    <div
      className={`flex h-[400px] items-center justify-center rounded-md border border-dashed px-6 text-center text-sm ${ring}`}
    >
      <p className="max-w-md">{children}</p>
    </div>
  );
}
